import "./sharingscreen.css";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import apiService from "../../services/api.service";
import Button from "../../components/ui/button";
import Loader from "../../components/ui/loader";
import DOMPurify from 'dompurify';
import queryString from 'query-string';
import Sanitizer from "../../utils/sanitizer";
import {
    buildFallbackCaptionFromLanguageContent,
    getConfiguredSocialShareCaption,
} from "../../utils/gamificationSocialShare";

const EMPTY_LANGUAGE_CONTENT = { v3: { gamification: {} } };

const getRewardImageByLanguage = (rewardImages, languageId) => {
    if (!rewardImages || typeof rewardImages !== 'object') return '';

    const languageKey = languageId != null ? String(languageId) : '';
    if (languageKey && rewardImages[languageKey]) {
        return rewardImages[languageKey];
    }

    return Object.values(rewardImages).find(Boolean) || '';
};

export default function SharingScreenExternal() {
    const params = useParams();
    const tenantId = params.teid;
    const tnxId = params.txid;
    const type = params.type;

    const [reward, setReward] = useState(0);
    const [rewardImage, setRewardImage] = useState('');
    const [caption, setCaption] = useState('');
    const [url, setUrl] = useState('');
    const [copied, setCopied] = useState(false);
    const [loader, setLoader] = useState(false);
    const [language, setLanguage] = useState(null);
    const [languageId, setLanguageId] = useState(null);
    const [socialShareConfig, setSocialShareConfig] = useState(null);

    useEffect(() => {
        if (language) {
            const sharingText =
                getConfiguredSocialShareCaption({
                    campaignConfig: { socialShare: socialShareConfig },
                    rewardAmount: reward,
                    languageId,
                    languageCode: language?.languagecode,
                }) || buildFallbackCaptionFromLanguageContent(language, reward);
            setCaption(sharingText);
            setUrl(language?.v3?.gamification?.sharingLink || "");
        }
    }, [language, languageId, reward, socialShareConfig]);

    const getLanguage = async (languageid) => {
        if (!languageid) {
            setLanguage(EMPTY_LANGUAGE_CONTENT);
            return;
        }

        try {
            const res = await apiService.get("/api/public/multilingual/" + languageid);
            setLanguage(JSON.parse(res.osakalanguagecontent));
        } catch (err) {
            console.error("Error loading language:", err);
            setLanguage(EMPTY_LANGUAGE_CONTENT);
        }
    };

    useEffect(() => {
        if (tenantId) {
            getWiningRewardDetails();
        }
    }, [tenantId]);

    const executeAction = async () => {
        await addSocialShareDetails();
        if (type === 'instagram') {
            const baseUrl = 'https://www.instagram.com/';
            const mediaUrl = encodeURIComponent(rewardImage);
            const instagramShareUrl = `${baseUrl}?${queryString.stringify({ url: mediaUrl, caption })}`;
            window.open(instagramShareUrl, '_blank');
        }
        if (type === 'twitter') {
            const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}${url ? `&url=${encodeURIComponent(url)}` : ''}`;
            window.open(twitterShareUrl, '_blank');
        }
    };

    const handleDownload = async () => {
        if (!rewardImage) return;

        const sanitizedUrl = DOMPurify.sanitize(rewardImage);

        try {
            const urlObject = new URL(sanitizedUrl);
            if (urlObject.protocol !== 'http:' && urlObject.protocol !== 'https:') {
                throw new Error('Invalid URL protocol');
            }

            const response = await fetch(sanitizedUrl);
            if (!response.ok) throw new Error('Failed to fetch download image');
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'VisaWariChanceCashback.png';

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    const getWiningRewardDetails = async () => {
        setLoader(true);
        try {
            const info = {
                tnxId: tnxId,
                tenantid: tenantId,
            };
            const res = await apiService.post("/api/gamification/get-wining-reward-details", info);
            if (res) {
                setReward(parseInt(res.rewardDetails?.rewardAmount || 0));
                setRewardImage(Sanitizer.sanitizeURL(getRewardImageByLanguage(res.rewardDetails?.rewardImage, res.langId)));
                setLanguageId(res.langId);
                setSocialShareConfig(res.rewardDetails?.socialShare || null);
                await getLanguage(res.langId);
            }
        } catch (error) {
            console.error("Error getting reward details:", error);
            setLanguage(EMPTY_LANGUAGE_CONTENT);
        } finally {
            setLoader(false);
        }
    };

    const addSocialShareDetails = async () => {
        try {
            const info = {
                tnxId: tnxId,
                tenantid: tenantId,
                platform: type === 'twitter' ? "X" : type,
                analytics: {
                    action: "Social Share",
                    externaluserid: "",
                    tenantid: tenantId,
                }
            };
            await apiService.post("/api/gamification/add-social-share-details", info);
        } catch (error) {
            console.error("Error adding social share details:", error);
        }
    };

    if (loader || !language) {
        return <Loader fullscreen label="Loading..." size="xl" />;
    }

    return (
        <div style={{ minHeight: '100vh', zIndex: 99, margin: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="align-center" style={{ marginTop: '10px', marginBottom: '10px' }}>
                <p style={{ fontSize: '18px', color: '#000000', fontWeight: 'bold', textAlign: 'center' }}>
                    {language?.v3?.gamification?.sharePostTitle || "Share Your Win!"}
                </p>
            </div>
            <div className="bg-gray-100 p-4 rounded" style={{ paddingBottom: copied ? '20px' : '10px', maxWidth: '600px', width: '100%', marginBottom: '10px' }}>
                <p
                    style={{ color: 'black', textAlign: 'center', whiteSpace: 'pre-line' }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(caption) }}
                />
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            navigator.clipboard.writeText(caption);
                            setCopied(true);
                            setTimeout(() => {
                                setCopied(false);
                            }, 2500);
                        }}
                    >
                        {language?.v3?.gamification?.copy || "Copy"}
                    </Button>
                    {copied && (
                        <p className="font-11" style={{ color: 'green', textAlign: 'center', margin: 0 }}>
                            {language?.v3?.gamification?.copiedToClipboard || "Copied to clipboard!"}
                        </p>
                    )}
                </div>
            </div>
            <div className="align-center" style={{ maxWidth: '600px', width: '100%' }}>
                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                    {rewardImage && <img src={rewardImage} alt="Reward" style={{ maxWidth: "100%", height: "auto", display: 'block', margin: '0 auto' }} />}
                </div>
                <div style={{ marginTop: '10px', marginBottom: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Button
                        variant="secondary"
                        onClick={handleDownload}
                        style={{
                            backgroundColor: '#000000',
                            color: '#FFFFFF',
                            borderRadius: '25px',
                            border: 'none'
                        }}
                    >
                        ↓ {language?.v3?.gamification?.downloadImage || "Download Image"}
                    </Button>
                </div>
                <div style={{ marginTop: '20px', marginBottom: '10px' }}>
                    <p style={{ color: 'black', textAlign: 'center', marginBottom: '10px' }}>
                        {language?.v3?.gamification?.sharingInstruction1 || "Download the image and share it on your social media"}<br />
                        {language?.v3?.gamification?.sharingInstruction2 || "Don't forget to tag us!"}
                    </p>
                    <div style={{ width: '100%', paddingLeft: '20px', paddingRight: '20px', display: 'flex', justifyContent: 'center' }}>
                        <Button variant="primary" onClick={executeAction} style={{ borderRadius: '25px', width: '100%' }}>
                            {language?.v3?.gamification?.sharePost || "Share Post"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
